import React, {useRef, useState} from 'react'

type BoardDragOptions<TCard, TStage extends string> = {
    stageOf: (card: TCard) => TStage
    // Whether `stage` takes `card`: only those columns call preventDefault, so
    // only they are drop targets.
    accepts: (card: TCard, stage: TStage) => boolean
    onDrop: (card: TCard, stage: TStage) => void
    // The card was let go after entering another column that refused it: the
    // browser puts it back, the board says why.
    onRefused: (card: TCard, stage: TStage) => void
}

export type ColumnDragProps = {
    onColumnDragEnter: () => void
    onColumnDragOver: (e: React.DragEvent) => void
    onColumnDragLeave: (e: React.DragEvent) => void
    onColumnDrop: (e: React.DragEvent) => void
    isDropTarget: boolean
}

// HTML5 drag and drop shared by both boards. The card's position inside a
// column is not kept: the backend orders by the next action's due date.
export function useBoardDrag<TCard extends {id: string}, TStage extends string>({stageOf, accepts, onDrop, onRefused}: BoardDragOptions<TCard, TStage>) {
    const [dragging, setDragging] = useState<TCard | null>(null)
    const [overStage, setOverStage] = useState<TStage | null>(null)
    // Last column the dragged card entered, and whether it landed on a target.
    const lastEntered = useRef<TStage | null>(null)
    const dropped = useRef(false)

    const startDrag = (card: TCard, e: React.DragEvent) => {
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', card.id)
        lastEntered.current = stageOf(card)
        dropped.current = false
        // Re-render after the browser has taken the drag image.
        setTimeout(() => setDragging(card), 0)
    }

    const endDrag = () => {
        const card = dragging
        const entered = lastEntered.current
        setDragging(null)
        setOverStage(null)
        lastEntered.current = null
        if (card && !dropped.current && entered && entered !== stageOf(card)) onRefused(card, entered)
    }

    const columnDragProps = (stage: TStage): ColumnDragProps => {
        const takes = !!dragging && stageOf(dragging) !== stage && accepts(dragging, stage)
        return {
            onColumnDragEnter: () => { if (dragging) lastEntered.current = stage },
            onColumnDragOver: (e: React.DragEvent) => {
                if (!takes) return
                e.preventDefault()
                e.dataTransfer.dropEffect = 'move'
                if (overStage !== stage) setOverStage(stage)
            },
            onColumnDragLeave: (e: React.DragEvent) => {
                if (overStage === stage && !e.currentTarget.contains(e.relatedTarget as Node | null)) setOverStage(null)
            },
            onColumnDrop: (e: React.DragEvent) => {
                if (!takes || !dragging) return
                e.preventDefault()
                dropped.current = true
                onDrop(dragging, stage)
            },
            isDropTarget: takes && overStage === stage,
        }
    }

    return {dragging, startDrag, endDrag, columnDragProps}
}
